import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import VideoFacade from '../components/VideoFacade';
import { COMPANY_INFO } from '../data/siteData';

export default function AboutPage() {
  return (
    <>
      <SEO
        title="Om Hærvejens Træpleje: En fortælling om passion, ekspertise og vækst"
        description="Historien om Hærvejens Træpleje og stifter Mathias Svendsen. Holdet af 12 dedikerede arborister og lærlinge fra Skovskolen."
        canonical="https://xn--hrvejenstrpleje-xlbj.dk/om-os/"
        ogImage="https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/mathias-svendsen-1-scaled-e1761900902121.jpg"
      />

      <Breadcrumbs items={[{ label: 'Om os' }]} />

      {/* Hero Header */}
      <section className="bg-[#10271c] text-white py-14 sm:py-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs font-bold text-[#7dd2a6] bg-[#1a3d2c] px-3.5 py-1.5 rounded-full inline-block mb-4">
            Vores historie
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            Om Hærvejens Træpleje: En fortælling om passion, ekspertise og vækst
          </h1>
          <p className="text-base sm:text-lg text-[#cadcd1] leading-relaxed max-w-2xl mx-auto">
            Fra Skovskolen til selvstændig drøm. Mød teamet på 12 mand, der brænder for sikkerhed, godt håndværk og træernes biologi.
          </p>
        </div>
      </section>

      {/* Main Story Section */}
      <section className="py-16 sm:py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-6 text-[#37493e] leading-relaxed text-base">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#14291d]">
              Fra Skovskole til selvstændig drøm
            </h2>

            <p>
              Hærvejens Træpleje er mere end bare en virksomhed – det er en passion for træer, drevet af et dynamisk hold af professionelle arborister. I spidsen står Mathias Svendsen, en ung iværksætter, som på kun tre år har skabt en af regionens mest anerkendte træplejevirksomheder med 12 dedikerede medarbejdere.
            </p>

            <p>
              Vi er et team af 10 uddannede arborister og 2 lærlinge, alle i alderen 20-30 år, som deler en fælles kærlighed til faget. Vores mission er at levere sikker og professionel træpleje med fokus på både træets sundhed og dine ønsker. Vi holder til i Christiansfeld og Vejle, men vores biler kører i hele Jylland og på Fyn for at løse opgaver for både private, erhverv og store forsyningsselskaber som Energinet.
            </p>

            <p>
              Hos os er du garanteret, at det er uddannede fagfolk, der kommer ud. Vi er stolte af vores håndværk og sætter en ære i at finde den bedste løsning for dig og dine træer.
            </p>

            <blockquote className="border-l-4 border-[#357959] bg-[#f4f8f5] p-5 rounded-r-xl italic text-[#263b2f] my-6">
              “Jeg har hele tiden vidst, at det skulle være noget med noget skov og sådan noget. Men at det lige var det her, det stødte jeg egentlig først på, da jeg så gik i gang med uddannelsen.”
              <span className="block not-italic font-bold text-xs text-[#14291d] mt-2">
                — Mathias Svendsen, Indehaver
              </span>
            </blockquote>

            <p>
              Efter 4 års specialiseret uddannelse på blandt andet Skovskolen i Nødebo, dimitterede Mathias som færdiguddannet arborist i 2019, kun 18 år gammel. Hans uddannelse var ikke den traditionelle skovarbejder-linje, men en moderne arborist-uddannelse med fokus på bytræer, rodnet i komplekse miljøer og avanceret sygdomsforståelse.
            </p>

            <p>
              Efter et par år som ansat i branchen, voksede frustrationen over at se faget blive kompromitteret. Da en projektleder uden faglig baggrund begyndte at diktere priser og metoder, var det dråben.
            </p>

            <blockquote className="border-l-4 border-[#357959] bg-[#f4f8f5] p-5 rounded-r-xl italic text-[#263b2f] my-6">
              “Jeg kunne ikke rigtig forliges med, at en mand, der ikke rigtig vidste, hvordan man skulle fælde træer, at han lige pludselig skulle bestemme priserne på træfældningen, og han skulle lige pludselig bestemme, hvordan det skulle udføres. Det blev jeg lidt irriteret over, og så ja, der startede jeg så selvstændigt.”
              <span className="block not-italic font-bold text-xs text-[#14291d] mt-2">
                — Mathias Svendsen
              </span>
            </blockquote>

            <p>
              I januar 2022, som 20-årig, stiftede Mathias Hærvejens Træpleje. Han startede alene, men med en klar vision: at levere træpleje af højeste kvalitet uden at gå på kompromis med sikkerhed eller faglighed. Rygtet om hans dygtighed spredte sig hurtigt, og i dag, kun få år senere, leder han et team på 12 mand, der håndterer alt fra private haver til komplekse opgaver langs højspændingsmaster for Energinet.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-2xl overflow-hidden border border-[#dce5df] shadow-xs">
              <img
                src="https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/mathias-svendsen-1-scaled-e1761900902121.jpg"
                alt="Mathias Svendsen - Stifter af Hærvejens Træpleje"
                className="w-full h-auto object-cover"
              />
              <div className="p-4 bg-white">
                <p className="font-bold text-base text-[#14291d]">Mathias Svendsen</p>
                <p className="text-xs text-[#527a65]">Indehaver & Certificeret Arborist fra Skovskolen</p>
              </div>
            </div>

            {/* Video embed facade from scrape */}
            <VideoFacade
              title="Træfældning af bøgetræer 🏡 Hærvejens Træpleje"
              videoId="ImKp0t-YVc4"
              provider="youtube"
              caption="Se Mathias og teamet udføre præcisionsarbejde i felten."
            />
          </div>
        </div>
      </section>

      {/* Hærvejsgruppen Partnerskab */}
      <section className="py-16 bg-white border-b border-[#dce5df]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#14291d]">
              Hærvejsgruppen – Holdet bag
            </h2>
            <p className="text-sm text-[#4c5f53] mt-2">
              Hærvejens Træpleje udgør sammen med Hærvejens Skovservice partnerskabet Hærvejsgruppen, som dækker meget bredt og i hele landet.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="bg-[#faf9f5] rounded-2xl p-6 border border-[#e4dfd2] text-center flex flex-col items-center">
              <img
                src="https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/mathias2-1024x1024.jpg"
                alt="Mathias Svendsen"
                className="w-24 h-24 rounded-full object-cover mb-4 border border-[#2d6a4f]"
              />
              <h3 className="font-bold text-base text-[#14291d]">Mathias Svendsen</h3>
              <p className="text-xs text-[#2d6a4f] font-semibold mb-2">Hærvejens Træpleje</p>
              <p className="text-xs text-[#48594e] leading-relaxed">
                Stifter af Hærvejens Træpleje og passioneret arborist fra Skovskolen i Nødebo. Leder det dedikerede hold af træplejere.
              </p>
            </div>

            <div className="bg-[#faf9f5] rounded-2xl p-6 border border-[#e4dfd2] text-center flex flex-col items-center">
              <img
                src="https://assets.auxodev.dk/assets/3d990fe3-1622-47f2-aa75-fd8032eebfed/img-1326-2-1024x1024-1.jpg"
                alt="Anne Blomme"
                className="w-24 h-24 rounded-full object-cover mb-4 border border-[#2d6a4f]"
              />
              <h3 className="font-bold text-base text-[#14291d]">Anne Blomme</h3>
              <p className="text-xs text-[#2d6a4f] font-semibold mb-2">Hærvejsgruppen Administration</p>
              <p className="text-xs text-[#48594e] leading-relaxed">
                Bogholder og ansvarlig for det administrative. Sikrer at både økonomi og drift kører gnidningsfrit for hele gruppen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values table from scrape */}
      <section className="py-16 max-w-5xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#14291d] text-center mb-8">
          Vores garanti til dig
        </h2>
        <div className="overflow-hidden rounded-2xl border border-[#d6e3db] shadow-xs">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#163627] text-white">
              <tr>
                <th className="py-4 px-5 font-bold w-1/3">Værdi:</th>
                <th className="py-4 px-5 font-bold w-2/3">Vores garanti til dig:</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e3ece6] bg-[#fafcfb]">
              <tr>
                <td className="py-4 px-5 font-semibold text-[#14291d]">Uddannede fagfolk</td>
                <td className="py-4 px-5 text-[#3b4d42]">
                  Alle vores medarbejdere er uddannede arborister fra Skovskolen eller er lærlinge på vej mod samme mål. Vi ansætter kun folk, vi kender og stoler på fagligt.
                </td>
              </tr>
              <tr>
                <td className="py-4 px-5 font-semibold text-[#14291d]">Kompromisløs sikkerhed</td>
                <td className="py-4 px-5 text-[#3b4d42]">
                  Vi overholder altid Arbejdstilsynets regler. Det betyder, at vi minimum er to mand på klatreopgaver, hvor personen på jorden er uddannet til at udføre en redningsaktion. Din sikkerhed og vores er vores førsteprioritet.
                </td>
              </tr>
              <tr>
                <td className="py-4 px-5 font-semibold text-[#14291d]">Fuld forsikring</td>
                <td className="py-4 px-5 text-[#3b4d42]">
                  Vi er fuldt forsikret for op til 10 millioner kroner pr. skade. Du kan være helt tryg, når vi arbejder på din grund.
                </td>
              </tr>
              <tr>
                <td className="py-4 px-5 font-semibold text-[#14291d]">Ungt og effektivt team</td>
                <td className="py-4 px-5 text-[#3b4d42]">
                  Vores team består af unge, energiske fagfolk i 20’erne, som er passionerede omkring deres arbejde. Vi er effektive, grundige og altid klar til at yde vores bedste.
                </td>
              </tr>
              <tr>
                <td className="py-4 px-5 font-semibold text-[#14291d]">Ærlighed og gennemsigtighed</td>
                <td className="py-4 px-5 text-[#3b4d42]">
                  Vi springer aldrig over, hvor gærdet er lavest. Vi giver dig ærlig rådgivning og et præcist tilbud, så du undgår uforudsete udgifter.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
